import { selMessage } from "@selector/shared";
import { produce } from "immer";
import { sortBy } from "lodash-es";

import { createErrorHandler } from "@/app-utils/errors.ts";
import { pushRoute } from "@/app-utils/route.ts";
import { type Capability, type InputRoleInput, type Role } from "@/graphql.ts";
import {
  CREATE_ROLE_FAILURE,
  CREATE_ROLE_REQUEST,
  CREATE_ROLE_SUCCESS,
  LOAD_CAPABILITIES_FAILURE,
  LOAD_CAPABILITIES_REQUEST,
  LOAD_CAPABILITIES_SUCCESS,
  LOAD_ROLES_FAILURE,
  LOAD_ROLES_REQUEST,
  LOAD_ROLES_SUCCESS,
  SET_LOADED_ROLE,
  UPDATE_ROLE_FAILURE,
  UPDATE_ROLE_REQUEST,
  UPDATE_ROLE_SUCCESS,
} from "@/redux/action-types.ts";

import type WebApiClient from "../../api/WebApiClient.js";
import { notify } from "../../modules/notifications/notify.js";
import { type IAppState } from "../reducer.js";
import { createReducer } from "../redux-utils.js";
import { loadUserCapabilities } from "./auth.js";

/**
 * State definition
 */

export interface IRolesAdminState {
  roles: Role[];
  loadedRole: Role;
  capabilities: Capability[];
  playbooks: Capability[];
}

const initialState: IRolesAdminState = {
  roles: [],
  loadedRole: null,
  capabilities: [],
  playbooks: [],
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [LOAD_ROLES_SUCCESS]: (state: IRolesAdminState, roles: Role[]): IRolesAdminState => {
    return { ...state, roles };
  },

  [LOAD_CAPABILITIES_SUCCESS]: (state: IRolesAdminState, allCapabilities: Capability[]): IRolesAdminState => {
    const playbooks = allCapabilities.filter((capability) => capability.type === "playbook" && capability.playbook);
    const capabilities = allCapabilities.filter((capability) => capability.type !== "playbook");

    return { ...state, playbooks, capabilities };
  },

  [SET_LOADED_ROLE]: (state: IRolesAdminState, role: Role): IRolesAdminState => {
    return { ...state, loadedRole: role };
  },

  [UPDATE_ROLE_SUCCESS]: (state: IRolesAdminState, role: Role): IRolesAdminState => {
    const idx: number = state.roles.findIndex((r: Role) => r.id === role.id);
    const newRoles = produce(state.roles, (draft) => {
      draft[idx] = role;
    });

    return { ...state, loadedRole: role, roles: newRoles };
  },

  [CREATE_ROLE_SUCCESS]: (state: IRolesAdminState, role: Role): IRolesAdminState => {
    // add new role to all roles and as loaded role
    let newRoles = produce(state.roles, (draft) => {
      draft.push(role);
    });
    newRoles = sortBy(newRoles, (r: Role) => r.name);

    return { ...state, loadedRole: role, roles: newRoles };
  },
});

/**
 * Action creators
 */
export function loadRoles() {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<Role[]> => {
    dispatch({ type: LOAD_ROLES_REQUEST });

    return webApiClient
      .fetchAllRoles()
      .then((roles: Role[]) => {
        // The list must be ordered by legacy first and then by name
        dispatch({
          type: LOAD_ROLES_SUCCESS,
          payload: roles,
        });
        return roles;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_ROLES_FAILURE,
          alertTitle: "Fetching of Roles Failed",
        }),
      );
  };
}

export function loadRole(role: Role) {
  return (dispatch): void => {
    dispatch({
      type: SET_LOADED_ROLE,
      payload: role,
    });
  };
}

export function loadAllCapabilities() {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<Capability[]> => {
    dispatch({ type: LOAD_CAPABILITIES_REQUEST });

    return webApiClient
      .fetchAllCapabilities()
      .then((ret: Capability[]) => {
        dispatch({
          type: LOAD_CAPABILITIES_SUCCESS,
          payload: ret,
        });
        return ret;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_CAPABILITIES_FAILURE,
          alertTitle: `Fetching of Capabilities Failed`,
        }),
      );
  };
}

export function updateRole(payload: InputRoleInput): any {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<Role> => {
    dispatch({ type: UPDATE_ROLE_REQUEST });
    //dispatch(auditModifyProject(payload));

    return webApiClient
      .updateRole(payload)
      .then((role: Role) => {
        dispatch({
          type: UPDATE_ROLE_SUCCESS,
          payload: role,
        });
        dispatch(loadRoles());
        // update the capabilties in case they affect the user itself
        dispatch(loadUserCapabilities());

        notify.info({
          title: selMessage({ id: "admin.role.modified.successTitle" }),
          children: selMessage({
            id: "admin.role.modified.successMessage",
            values: {
              roleName: role.name,
            },
          }),
        });
        // change route in case we renamed the role
        pushRoute(`/admin/roles/${role.name}`);
        return role;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: UPDATE_ROLE_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function createRole(payload: InputRoleInput): any {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<Role> => {
    dispatch({ type: CREATE_ROLE_REQUEST });
    //dispatch(auditModifyProject(payload));

    return webApiClient
      .createRole(payload)
      .then((role: Role) => {
        dispatch({
          type: CREATE_ROLE_SUCCESS,
          payload: role,
        });
        notify.info({
          title: selMessage({ id: "admin.role.created.successTitle" }),
          children: selMessage({
            id: "admin.role.created.successMessage",
            values: {
              roleName: role.name,
            },
          }),
        });
        pushRoute(`/admin/roles/${role.name}`);
        return role;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: CREATE_ROLE_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}
